// Caller-side wiring helpers for the reactive consumption mode (see
// RULES-ENGINE-DESIGN.md). The evaluator is pure and re-runs on every state
// change, but a viewer (Unity) is imperative — it must be *pushed* material
// changes, and re-pushing every forced pick on every evaluation would flood
// it. These helpers compute the minimal push set between two evaluations.
// Pure data-in/data-out (no React, no viewer handle) so the contract is
// unit-testable without a DOM harness.

/** One viewer push the caller owes after an evaluation: apply the forced
 *  material, or — when a force cleared — restore the canonical pick. */
export interface ForcedBindingDelta {
  surfaceKey: string;
  /** The material to push: the newly-forced pick, or the canonical pick a
   *  cleared force uncovers. Null when a force cleared and the caller knows
   *  no canonical pick for the surface — nothing sensible to push, so the
   *  caller skips it (the surface keeps whatever the viewer last showed). */
  materialId: string | null;
}

/** Diff two evaluations' forced picks for one instance into the minimal set
 *  of viewer pushes. `prevForced` is what the caller last *applied* (its
 *  per-instance ref), `nextForced` is the fresh overlay's forcedPicks
 *  (surfaceKey → materialId), and `canonicalPicks` resolves what a cleared
 *  force should restore (user pick, else authored default — the caller
 *  pre-resolves defaults since only it holds the product detail). Output is
 *  sorted by surfaceKey so push order is deterministic. */
export function computeForcedBindingDelta(
  prevForced: Record<string, string>,
  nextForced: Record<string, string>,
  canonicalPicks: Record<string, string>,
): ForcedBindingDelta[] {
  const delta: ForcedBindingDelta[] = [];
  // New or changed forces — push the forced material.
  for (const surfaceKey of Object.keys(nextForced)) {
    if (prevForced[surfaceKey] !== nextForced[surfaceKey]) {
      delta.push({ surfaceKey, materialId: nextForced[surfaceKey] });
    }
  }
  // Cleared forces — push the canonical pick back (the force evaporated with
  // its trigger; the user's own choice becomes visible again).
  for (const surfaceKey of Object.keys(prevForced)) {
    if (!(surfaceKey in nextForced)) {
      delta.push({ surfaceKey, materialId: canonicalPicks[surfaceKey] ?? null });
    }
  }
  return delta.sort((a, b) => a.surfaceKey.localeCompare(b.surfaceKey));
}
